import React, { useEffect, useState } from 'react';
import { useLocation, useRoute } from 'wouter';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useToast } from "@/hooks/use-toast";


import { supabase } from '../../supabaseClient';
import { Description } from '@radix-ui/react-toast';
import { Contact } from 'lucide-react';
import { SelectContent, SelectItem } from '@/components/ui/select';
import FileUpload from '@/components/ui/fileupload';


const RoleDetails = () => {
  const [match, params] = useRoute('/role-details/:roleId');
  const [, navigate] = useLocation();
  const [role, setRole] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formValues, setFormValues] = useState({ title: '', location: '', job_type: '', description: '', total_positions: '', min_experience_required: '', skills_required: '', salary_range: '', contact_person: '', contact_person_email: '', job_posted_date: '', job_apply_by_date: '', status: '', notice_period: '', vendor_id: '', hr_id: '', remarks: '' });

  const [availableVendors, setAvailableVendors] = useState([]);
  const [availableHr, setAvailableHr] = useState([]);
  const { toast } = useToast();
  const statuses = ['Open', 'In Progress', 'On Hold', 'Successfully Placed', 'Closed (Unfilled)', 'Offer Made']

  const roleId = params?.roleId;

  useEffect(() => {
    const fetchRoleDetails = async () => {
      if (!roleId) {
        setError('Role ID is missing.');
        setLoading(false);
        return;
      }

      try {
        const { data: roleData, error: roleError } = await supabase
          .from('projects')
          .select('*, vendors(name), hr(name)')
          .eq('id', roleId)
          .single();

        if (roleError) {
          setError(roleError.message);
        } else {
          setRole(roleData);
          setFormValues({
            title: roleData.title,
            location: roleData.location,
            job_type: roleData.job_type,
            description: roleData.description,
            total_positions: roleData.total_positions,
            min_experience_required: roleData.min_experience_required,
            skills_required: roleData.skills_required,
            salary_range: roleData.salary_range,
            contact_person: roleData.contact_person,
            contact_person_email: roleData.contact_person_email,
            job_posted_date: roleData.job_posted_date,
            job_apply_by_date: roleData.job_apply_by_date,
            status: roleData.status,
            notice_period: roleData.notice_period,
            vendor_id: roleData.vendor_id,
            hr_id: roleData.hr_id,
            remarks: roleData.remarks
          });
        }

        const { data: candidateData, error: candidateError } = await supabase
          .from('project_candidate')
          .select('candidate_id, joining_date, offered_salary, status, candidates(name)')
          .eq('project_id', roleId);

        if (candidateError) {
          setError(candidateError.message);
        } else {
          setCandidates(candidateData || []);
        }
      } catch (err) {
        setError('Error fetching Role details');
      } finally {
        setLoading(false);
      }
    };

    const fetchVendors = async () => {
      try {
        const { data, error } = await supabase.from('vendors').select('id, name');
        if (error) {
          setError(error.message);
        } else {
          setAvailableVendors(data || []);
        }
      } catch (err) {
        setError('Error fetching Vendors.');
      }
    };

    const fetchHr = async () => {
      try {
        const { data, error } = await supabase.from('hr').select('id, name');
        if (error) {
          setError(error.message);
        } else {
          setAvailableHr(data || []);
        }
      } catch (err) {
        setError('Error fetching Hr.');
      }
    };

    fetchHr();

    fetchVendors();

    fetchRoleDetails();
  }, [roleId]);
  const handleDialogOpen = () => setDialogOpen(true);
  const handleDialogClose = () => setDialogOpen(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleBack = () => window.history.back();
  const handleAddCandidate = () => {
    navigate(`/search-candidates/${roleId}?location=${encodeURIComponent(formValues.location)}&skills=${encodeURIComponent(formValues.skills_required)}`);
  }
  const handleCandidateClick = (candidateId) => navigate(`/candidate-details/${candidateId}`);
  const handleUpdateCandidate = async (candidate) => {
    try {
      const { error } = await supabase
        .from('project_candidate')
        .update({
          joining_date: candidate.joining_date,
          offered_salary: candidate.offered_salary,
          status: candidate.status,
        })
        .eq('candidate_id', candidate.candidate_id)
        .eq('project_id', roleId);

      if (error) {
        toast({
          title: 'Error updating candidate details: ', status: 'error', duration: 1000,
          isClosable: true
        });
      } else {
        toast({
          title: 'Candidate details updated successfully!', status: 'success', duration: 1000,
          isClosable: true
        });
      }
    } catch (err) {
      toast({
        title: 'Unexpected error occurred.', status: 'error', duration: 1000,
        isClosable: true
      });
    }
  };

  const handleUpdate = async () => {
    try {
      const updatedValues = {
        title: formValues.title,
        location: formValues.location,
        job_type: formValues.job_type,
        description: formValues.description,
        total_positions: formValues.total_positions && !isNaN(parseInt(formValues.total_positions)) ? parseInt(formValues.total_positions) : null,
        min_experience_required: formValues.min_experience_required && !isNaN(parseInt(formValues.min_experience_required)) ? parseInt(formValues.min_experience_required) : null,
        skills_required: Array.isArray(formValues.skills_required) ? formValues.skills_required : [],
        salary_range: formValues.salary_range,
        contact_person: formValues.contact_person,
        contact_person_email: formValues.contact_person_email,
        job_posted_date: formValues.job_posted_date ? formValues.job_posted_date.trim() : null,
        job_apply_by_date: formValues.job_apply_by_date ? formValues.job_apply_by_date.trim() : null,
        status: formValues.status,
        notice_period: formValues.notice_period,
        vendor_id: formValues.vendor_id || null,
        hr_id: formValues.hr_id || null,
        remarks: formValues.remarks
      };


      const { data, error } = await supabase
        .from('projects')
        .update(updatedValues)
        .eq('id', roleId)
        .select();

      if (error) {
        console.error('Supabase Error:', error);
        setError(error.message);
      } else {
        console.log('Update Response:', data);
        setRole(data[0]);
        setDialogOpen(false);
        window.location.reload();
      }
    } catch (err) {
      console.error('Unexpected Error:', err);
      setError('Error updating role details.');
    }

  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  if (!role) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography>No role details found.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Role Details
        </Typography>
        <Box>
          <Button
            variant="contained"
            onClick={handleDialogOpen}
            sx={{
              backgroundColor: 'black',
              color: 'white',
              '&:hover': {
                backgroundColor: '#333',
              },
              mr: 1,
            }}
          >
            Update
          </Button>
          <Button
            variant="contained"
            onClick={handleBack}
            sx={{
              backgroundColor: 'black',
              color: 'white',
              '&:hover': {
                backgroundColor: '#333',
              },
            }}
          >
            Back
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            {role?.title}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {/* role details grid */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Vendor Name:
              </Typography>
              <Typography
                variant="body1"
                component="a"
                href={`/vendor-details/${role.vendor_id}`}
                style={{ color: '#1976d2', textDecoration: 'none' }}
              >
                {role.vendors?.name || 'Vendor Name Not Found'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                HR Name:
              </Typography>
              <Typography
                variant="body1"
                component="a"
                href={`/hr-details/${role.hr_id}`} // Navigate to the HR details page
                style={{ color: '#1976d2', textDecoration: 'none' }}
              >
                {role.hr?.name || 'HR Name Not Found'}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Location:
              </Typography>
              <Typography variant="body1">{role.location}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Job Type:
              </Typography>
              <Typography variant="body1">{role.job_type}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Description:
              </Typography>
              <Typography variant="body1">{role.description}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Total Positions:
              </Typography>
              <Typography variant="body1">{role.total_positions}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Min Experience Required:
              </Typography>
              <Typography variant="body1">{role.min_experience_required}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Skills Required:
              </Typography>
              <Typography variant="body1">{role.skills_required}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Salary Range:
              </Typography>
              <Typography variant="body1">{role.salary_range}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Contact Person:
              </Typography>
              <Typography variant="body1">{role.contact_person}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Contact Person Email:
              </Typography>
              <Typography variant="body1">{role.contact_person_email}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Job Posted Date:
              </Typography>
              <Typography variant="body1">{role.job_posted_date}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Job Apply By Date:
              </Typography>
              <Typography variant="body1">{role.job_apply_by_date}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Status:
              </Typography>
              <Typography variant="body1">{role.status}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Notice Period:
              </Typography>
              <Typography variant="body1">{role.notice_period}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Remarks:
              </Typography>
              <Typography variant="body1">{role.remarks}</Typography>
            </Grid>
          </Grid>

          <Divider sx={{ mt: 4 }} />

          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mt={2}>
            <Box>
              <Button
                variant="contained"
                onClick={handleAddCandidate}
                sx={{
                  backgroundColor: 'black',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#333',
                  },
                }}
              >
                Add Candidate
              </Button>
            </Box>
          </Box>
          <Typography variant="h6" sx={{ mt: 3, fontWeight: 'bold' }}>
            Assigned Candidates
          </Typography>
          <br />
          <Box sx={{ padding: '16px 0px' }}>
            {candidates.length > 0 ? (
              <Grid container spacing={2}>
                <Grid container spacing={2} alignItems="center" sx={{ fontWeight: 'bold' }}>
                  <Grid item xs={3}>
                    <Typography sx={{ fontWeight: 'bold' }}>Name</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography sx={{ fontWeight: 'bold' }}>Joining Date</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography sx={{ fontWeight: 'bold' }}>Offered Salary</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography sx={{ fontWeight: 'bold' }}>Status</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography sx={{ fontWeight: 'bold' }}>Actions</Typography>
                  </Grid>
                </Grid>
                <hr style={{ width: '100%', margin: '8px 0' }} />
                {/* Table Body */}
                {candidates.map((candidate) => (
                  <React.Fragment key={candidate.candidate_id}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={3}>
                        <Typography>{candidate.candidates?.name}</Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          type="date"
                          fullWidth
                          value={candidate.joining_date || ''}
                          placeholder="Select Date"
                          onChange={(e) =>
                            setCandidates((prev) =>
                              prev.map((c) =>
                                c.candidate_id === candidate.candidate_id
                                  ? { ...c, joining_date: e.target.value }
                                  : c
                              )
                            )
                          }
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'black',
                              },
                            },
                            '& label.Mui-focused': {
                              color: 'black',
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          label="Offered Salary"
                          fullWidth
                          value={candidate.offered_salary || ''}
                          onChange={(e) =>
                            setCandidates((prev) =>
                              prev.map((c) =>
                                c.candidate_id === candidate.candidate_id
                                  ? { ...c, offered_salary: e.target.value }
                                  : c
                              )
                            )
                          }
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'black',
                              },
                            },
                            '& label.Mui-focused': {
                              color: 'black',
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <Select
                          fullWidth
                          value={candidate.status || ''}
                          onChange={(e) =>
                            setCandidates((prev) =>
                              prev.map((c) =>
                                c.candidate_id === candidate.candidate_id
                                  ? { ...c, status: e.target.value }
                                  : c
                              )
                            )
                          }
                          displayEmpty
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'black',
                              },
                            },
                            '& .MuiSelect-select:focus': {
                              backgroundColor: 'transparent',
                            },
                            '& .MuiSelect-icon': {
                              color: 'black',
                            },
                          }}
                        >
                          <MenuItem value="">Choose Status</MenuItem>
                          <MenuItem value="">Rejected</MenuItem>
                          <MenuItem value="L1 Rejected">L1 Rejected</MenuItem>
                          <MenuItem value="Final/L2 Reject">Final/L2 Reject</MenuItem>
                          <MenuItem value="Selected">Selected</MenuItem>
                          <MenuItem value="Feedback Awaited">Feedback Awaited</MenuItem>
                          <MenuItem value="On hold">On hold</MenuItem>
                          
                         
                        </Select>

                      </Grid>
                      <Grid item xs={3} sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleUpdateCandidate(candidate)}
                          sx={{
                            padding: '11.5px 14px',
                            backgroundColor: 'black',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: '#333',
                            },
                          }}
                        >
                          Update
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleCandidateClick(candidate.candidate_id)}
                          sx={{
                            padding: '11.5px 0px',
                            backgroundColor: 'black',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: '#333',
                            },
                          }}
                        >
                          View Details
                        </Button>            
                      </Grid>
                    </Grid>
                    <hr style={{ width: '100%', margin: '4px 0' }} />
                  </React.Fragment>
                ))}
              </Grid>
            ) : (
              <Typography>No candidates assigned.</Typography>
            )}
             <FileUpload basepath={'role/'+role.id+'/'}/>
          </Box>

        </CardContent>
      </Card>


      {/* Update Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>Update Role Details</DialogTitle>
        <DialogContent>

          <TextField
            select
            label="Vendors"
            name="vendor_id"
            fullWidth
            margin="normal"
            value={formValues.vendor_id || ''}
            onChange={handleInputChange}
            SelectProps={{
              native: true,
            }}
          >
            <option value="" disabled>
              Select an Vendor
            </option>
            {availableVendors.map((vendor) => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.name}
              </option>
            ))}
          </TextField>
          <TextField
            select
            label="Hr"
            name="hr_id"
            fullWidth
            margin="normal"
            value={formValues.hr_id || ''}
            onChange={handleInputChange}
            SelectProps={{
              native: true,
            }}
          >
            <option value="" disabled>
              Select an HR
            </option>
            {availableHr.map((hr) => (
              <option key={hr.id} value={hr.id}>
                {hr.name}
              </option>
            ))}
          </TextField>
          <TextField
            label="Role Name"
            name="title"
            fullWidth
            margin="normal"
            value={formValues.title}
            onChange={handleInputChange}
          />
          <TextField
            label="Location"
            name="location"
            fullWidth
            margin="normal"
            value={formValues.location}
            onChange={handleInputChange}
          />
          <TextField
            label="Job Type"
            name="job_type"
            fullWidth
            margin="normal"
            value={formValues.job_type}
            onChange={handleInputChange}
          />
          <TextField
            label="Description"
            name="description"
            fullWidth
            margin="normal"
            value={formValues.description}
            onChange={handleInputChange}
          />
          <TextField
            label="Total Positions"
            name="total_positions"
            fullWidth
            margin="normal"
            value={formValues.total_positions}
            onChange={handleInputChange}
          />
          <TextField
            label="Minimun Experience"
            name="min_experience_required"
            fullWidth
            margin="normal"
            value={formValues.min_experience_required}
            onChange={handleInputChange}
          />
          <TextField
            label="Skills Required"
            name="skills_required"
            fullWidth
            margin="normal"
            value={formValues.skills_required}
            onChange={handleInputChange}
          />
          <TextField
            label="Notice Period"
            name="notice_period"
            fullWidth
            margin="normal"
            value={formValues.notice_period}
            onChange={handleInputChange}
          />

          <TextField
            label="Salary Range"
            name="salary_range"
            fullWidth
            margin="normal"
            value={formValues.salary_range}
            onChange={handleInputChange}
          />

          <TextField
            label="Contact Person"
            name="contact_person"
            fullWidth
            margin="normal"
            value={formValues.contact_person}
            onChange={handleInputChange}
          />

          <TextField
            label="Contact Person Email"
            name="contact_person_email"
            fullWidth
            margin="normal"
            value={formValues.contact_person_email}
            onChange={handleInputChange}
          />

          <TextField
            label="Job Posted Date"
            name="job_posted_date"
            type="date"
            fullWidth
            margin="normal"
            value={formValues.job_posted_date}
            onChange={handleInputChange}
          />

          <TextField
            label="Job Apply By Date"
            name="job_apply_by_date"
            type="date"
            fullWidth
            margin="normal"
            value={formValues.job_apply_by_date}
            onChange={handleInputChange}
          />
          <TextField
            select
            label="Status"
            name="status"
            fullWidth
            margin="normal"
            value={formValues.status || ''}
            onChange={handleInputChange}
            SelectProps={{
              native: true,
            }}
          >
            <option value="" disabled>
              Select a Status
            </option>
            {statuses.map((status, index) => (
              <option key={index} value={status}>
                {status}
              </option>
            ))}
          </TextField>
          <TextField
            label="Remarks"
            name="remarks"
            fullWidth
            margin="normal"
            value={formValues.remarks}
            onChange={handleInputChange}
          />


        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="secondary"
            sx={{
              backgroundColor: 'black',
              color: 'white',
              '&:hover': {
                backgroundColor: '#333',
              },
              mr: 1,
            }}>
            Cancel
          </Button>
          <Button onClick={handleUpdate} color="primary" variant="contained"
            sx={{
              backgroundColor: 'black',
              color: 'white',
              '&:hover': {
                backgroundColor: '#333',
              },
              mr: 1,
            }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RoleDetails;
